import { queryOptions, useQuery } from '@tanstack/react-query';

import { QueryOptions } from '@cast/shared-types';
import { makeIsEnabled } from '@cast/utils';
import { ValueReportAdoptionEventsResponse } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

const CLUSTER_ADOPTION_EVENTS_QUERY_KEY = 'cluster-adoption-events';

const getClusterAdoptionEventsQueryKey = ({
  organizationId,
  clusterId,
}: {
  organizationId: string | undefined;
  clusterId: string | undefined;
}) => [CLUSTER_ADOPTION_EVENTS_QUERY_KEY, organizationId, clusterId] as const;

type UseClusterAdoptionEventsQueryArgs<SELECT> = QueryOptions<
  ValueReportAdoptionEventsResponse,
  SELECT
> & {
  clusterId: string | undefined;
};

const getClusterAdoptionEventsQueryOptions = <
  SELECT = ValueReportAdoptionEventsResponse
>(
  {
    clusterId,
    enabled = true,
    ...rest
  }: UseClusterAdoptionEventsQueryArgs<SELECT>,
  organizationId: string | undefined
) =>
  queryOptions({
    queryKey: getClusterAdoptionEventsQueryKey({
      organizationId,
      clusterId,
    }),
    queryFn: async () => {
      const { data } = await apiClient.costReport.getClusterAdoptionEvents({
        organizationId: organizationId ?? '',
        clusterId: clusterId ?? '',
      });
      return data;
    },
    enabled: makeIsEnabled(!!organizationId, !!clusterId, enabled),
    ...rest,
  });

export const useClusterAdoptionEventsQuery = <
  SELECT = ValueReportAdoptionEventsResponse
>(
  args: UseClusterAdoptionEventsQueryArgs<SELECT>
) => {
  const { currentOrganization } = useOrganizations();

  return useQuery(
    getClusterAdoptionEventsQueryOptions(args, currentOrganization?.id)
  );
};
